import {COUNTRY_BE, COUNTRY_NL} from "../domain/CountryModel/constants/CountryConstants";
import {BoLoginFlow} from "../services/BoLoginFlow";

// Redirect loop guard: the legacy login hands React a fresh token, if React keeps bouncing back to the
// legacy within a few seconds something is structurally wrong (wrong DB, broken token...) and retrying
// forever only hammers the legacy and fills t_tokens.
const REBO_REDIRECT_GUARD_KEY = "rebo_redirect_attempts";
const REBO_REDIRECT_WINDOW_MS = 20_000;
const REBO_REDIRECT_MAX_ATTEMPTS = 3;

// Several failing requests can ask for a redirect at the same time, only the first one must go through.
let reboRedirectInFlight = false;

function readRedirectAttempts(): number[] {
    try {
        const raw = sessionStorage.getItem(REBO_REDIRECT_GUARD_KEY);
        const attempts: number[] = raw ? JSON.parse(raw) : [];
        const now = Date.now();
        return attempts.filter((t) => now - t < REBO_REDIRECT_WINDOW_MS);
    } catch {
        return [];
    }
}

function writeRedirectAttempts(attempts: number[]) {
    try {
        sessionStorage.setItem(REBO_REDIRECT_GUARD_KEY, JSON.stringify(attempts));
    } catch {
        // sessionStorage unavailable: the guard is best effort
    }
}

export const RouteUtils = {
    redirectToHomePage() {
        return '/dashboard';
    },

    getCountryId(): number {
        const context = RouteUtils.getContext();
        return context === 'nl' || context === 'vbk' ? COUNTRY_NL : COUNTRY_BE;
    },

    getContext() {

        // Apps with their own dedicated dev server/build (e.g. ReactGovexBackoffice on port
        // 3002) declare their context directly via VITE_APP_CONTEXT instead of relying on a
        // /backoffice-{context} URL prefix, which only ReactBackoffice needs since it serves
        // be/nl/ci/vbk from a single shared dev server.
        if (import.meta.env.VITE_APP_CONTEXT) {
            return import.meta.env.VITE_APP_CONTEXT;
        }

        if (window.location.hostname.includes('localhost')) {
            if (window.location.pathname.includes('backoffice-be')) {
                return 'be';
            } else if (window.location.pathname.includes('backoffice-nl')) {
                return 'nl';
            } else if (window.location.pathname.includes('backoffice-ci')) {
                return 'ci';
            } else if (window.location.pathname.includes('backoffice-vbk')) {
                return 'vbk';
            } else if (window.location.pathname.includes('backoffice-govex')) {
                return 'govex'
            }

            return null;

        } else if (window.location.hostname.includes('staging.admin')) {
            if (window.location.hostname.includes('staging.admin.bobex.be')) {
                return 'be';
            } else if (window.location.hostname.includes('staging.admin.bobex.nl')) {
                return 'nl';
            } else if (window.location.hostname.includes('staging.admin.conversion-impact.be')) {
                return 'ci';
            } else if (window.location.hostname.includes('staging.admin.verbouwkosten.com')) {
                return 'vbk';
            } else if (window.location.hostname.includes('staging.admin.govex.be')) {
                return 'govex';
            }
        } else {
            if (window.location.hostname.includes('admin.bobex.be')) {
                return 'be';
            } else if (window.location.hostname.includes('admin.bobex.nl')) {
                return 'nl';
            } else if (window.location.hostname.includes('admin.conversion-impact.be')) {
                return 'ci';
            } else if (window.location.hostname.includes('admin.verbouwkosten.com')) {
                return 'vbk';
            } else if (window.location.hostname.includes('admin.govex.be')) {
                return 'govex';
            }
        }

        return null;
    },

    checkIfLocalhost() {

        if (window.location.hostname.includes('staging')) {
            RouteUtils.getContext();
        }

        return window.location.hostname;
    },

    picturePath(context: string) {

        switch (context) {
            case 'be':
                return 'https://admin.bobex.be';
            case 'nl':
                return 'https://admin.bobex.nl';
            case 'ci':
                return 'https://admin.conversion-impact.be';
            case 'vbk':
                return 'https://admin.verbouwkosten.com';
            default:
                return 'http://localhost';
        }
    },

    csvFileName() {

        if (RouteUtils.getContext() === 'be') {
            return 'bobexbe_export';
        } else if (RouteUtils.getContext() === 'nl') {
            return 'bobexnl_export';
        }
    },

    getBasename() {
        if (import.meta.env.VITE_APP_CONTEXT) {
            return "";
        }
        if (window.location.hostname.includes('localhost')) {
            return '/backoffice-' + RouteUtils.getContext();
        }else {
            return "";
        }
    },
    getBackofficeServletBasenameForApiCalls() {
        if (window.location.hostname.includes('localhost')) {
            switch (RouteUtils.getContext()) {
                case "be":
                    return 'http://localhost/backoffice/control';
                case "nl":
                    return 'http://localhost/backofficenl/control';
                case "ci":
                    return 'http://localhost/cibo/control';
                case "govex":
                    return 'http://localhost/govexbo/control';
            }
        } else if (window.location.hostname.includes('staging.admin') ) {
            switch (RouteUtils.getContext()) {
                case "be":
                    return 'https://staging.admin.bobex.be/backoffice/control';
                case "nl":
                    return 'https://staging.admin.bobex.nl/backofficenl/control';
                case "ci":
                    return 'https://staging.admin.conversion-impact.be/cibo/control';
                case "govex":
                    return 'https://staging.admin.govex.be/govexbo/control';
            }
        } else {
            switch (RouteUtils.getContext()) {
                case "be":
                    return 'https://admin.bobex.be/backoffice/control';
                case "nl":
                    return 'https://admin.bobex.nl/backofficenl/control';
                case "ci":
                    return 'https://admin.conversion-impact.be/cibo/control';
                case "govex":
                    return 'https://admin.govex.be/govexbo/control';
            }
        }
    },

    getBackofficeServletBasename() {
        if (window.location.hostname.includes('localhost')) {
            switch (RouteUtils.getContext()) {
                case "be":
                    return 'http://localhost/backoffice/control';
                case "nl":
                    return 'http://localhost/backofficenl/control';
                case "ci":
                    return 'http://localhost/cibo/control';
                case 'govex':
                    return 'http://localhost/govexbo/control';
            }
        } else {
            const origin = window.location.origin || `https://${window.location.hostname}`;

            switch (RouteUtils.getContext()) {
                case "be":
                    return `${origin}/backoffice/control`;
                case "nl":
                    return `${origin}/backofficenl/control`;
                case "ci":
                    return `${origin}/cibo/control`;
                case 'govex':
                    return `${origin}/govexbo/control`;
            }
        }
    },

    getExtranetName() {
        const serverName = window.location.hostname;
        const context = RouteUtils.getContext();

        if (serverName === "localhost") {
            return `http://localhost:3001/extranet-${context}`;
        }

        // Déterminer le préfixe (staging.my pour staging, my pour prod)
        const prefix = serverName.includes('staging.my') ? 'staging.my' : 'my';

        switch (context) {
            case "be":
                return `https://${prefix}.bobex.be`;
            case "nl":
                return `https://${prefix}.bobex.nl`;
            case "ci":
                return `https://${prefix}.conversion-impact.be`;
            case "vbk":
                return `https://${prefix}.verbouwkosten.com`;
            case 'govex':
                return `https://${prefix}.govex.be`;
            default:
                return `https://${prefix}.bobex.be`;
        }
    },

    getAlwaysProdUrl(): string | null {
        switch (RouteUtils.getContext()) {
            case "be":
                return 'admin.bobex.be';
            case "nl":
                return 'admin.bobex.nl';
            case "ci":
                return 'admin.conversion-impact.be';
            case "govex":
                return 'admin.govex.be';
        }

        return null;
    },

    filePath() {
        const context = RouteUtils.getContext();

        if (window.location.hostname.includes('localhost')) {
            return 'http://localhost';
        } else if (window.location.hostname.includes('staging.admin')) {
            switch (context) {
                case 'be':
                    return 'https://staging.admin.bobex.be';
                case 'nl':
                    return 'https://staging.admin.bobex.nl';
                case 'ci':
                    return 'https://staging.admin.conversion-impact.be';
                case 'vbk':
                    return 'https://staging.admin.verbouwkosten.com';
                case "govex":
                    return 'https://staging.admin.govex.be';
                default:
                    return 'https://staging.admin.bobex.be';
            }
        } else {
            // Production
            switch (context) {
                case 'be':
                    return 'https://admin.bobex.be';
                case 'nl':
                    return 'https://admin.bobex.nl';
                case 'ci':
                    return 'https://admin.conversion-impact.be';
                case 'vbk':
                    return 'https://admin.verbouwkosten.com';
                case 'govex':
                    return 'https://admin.govex.be';
                default:
                    return 'https://admin.bobex.be';
            }
        }
    },

    shouldShowFrLinks(): boolean {
        const context = this.getContext();
        return context !== 'nl' && context !== 'vbk';
    },


    /** Call once a login succeeded so the loop guard starts from zero again. */
    resetReboRedirectGuard() {
        writeRedirectAttempts([]);
        reboRedirectInFlight = false;
    },

    /**
     * Send the user through the legacy backoffice login, which comes back here with a fresh token.
     *
     * @returns true if the browser is being redirected, false if the redirect was skipped (a bo-login is
     * running, another redirect is already on its way, or the loop guard tripped). Callers that get false
     * from a failed login should show an error instead of retrying.
     */
    reboRedirect(): boolean {
        // A bo-login is running: its own result decides what happens next, redirecting now would abort it
        // (and burn the one-shot token Spring may already have consumed).
        if (BoLoginFlow.isInProgress() || reboRedirectInFlight) {
            return false;
        }

        const attempts = readRedirectAttempts();
        if (attempts.length >= REBO_REDIRECT_MAX_ATTEMPTS) {
            console.error(
                `[reboRedirect] ${attempts.length} legacy redirects in ${REBO_REDIRECT_WINDOW_MS / 1000}s, stopping to avoid a login loop.`
            );
            return false;
        }
        writeRedirectAttempts([...attempts, Date.now()]);
        reboRedirectInFlight = true;

        const url = new URL(window.location.href);

        // Remove token param if present
        url.searchParams.delete("token");

        let currentPagePath = url.pathname + url.search + url.hash;

        // If localhost with "backoffice-{context}", strip it out
        const backofficePrefix = "/backoffice-" + RouteUtils.getContext() + "/";
        if (currentPagePath.startsWith(backofficePrefix)) {
            currentPagePath = currentPagePath.substring(backofficePrefix.length);
        } else if (currentPagePath.startsWith("/")) {
            // For prod: just drop the leading slash
            currentPagePath = currentPagePath.substring(1);
        }

        const encodedRedirect = btoa(currentPagePath);

        window.location.href =
            RouteUtils.getBackofficeServletBasename() +
            "/react_backoffice_login?redirect=" +
            encodedRedirect;

        return true;
    },

    /**
     * Retourne l'URL de base du React Backoffice en fonction du contexte fourni
     * Gère les environnements : localhost, staging , et production
     *
     * @param context - Le contexte cible : 'be', 'nl', 'ci', 'vbk', ou 'govex'
     * @returns L'URL de base du React Backoffice (ex: https://staging.admin.bobex.be/ ou http://localhost:3000/backoffice-be/)
     */
    getReactBackofficeBaseUrl(context: "BE" | "CI" | "NL"): string {
        const hostname = window.location.hostname;


        // Environnement localhost
        if (hostname.includes('localhost')) {
            switch (context.toLowerCase()) {
                case 'be':
                    return 'http://localhost:3000/backoffice-be/';
                case 'nl':
                    return 'http://localhost:3000/backoffice-nl/';
                case 'ci':
                    return 'http://localhost:3000/backoffice-ci/';
                case 'vbk':
                    return 'http://localhost:3000/backoffice-vbk/';
                case 'govex':
                    return 'http://localhost:3002/';
                default:
                    return 'http://localhost:3000/';
            }
        }
        // Environnement staging (staging.my)
        else if (hostname.includes('staging.admin')) {
            switch (context.toLowerCase()) {
                case 'be':
                    return 'https://staging.admin.bobex.be/';
                case 'nl':
                    return 'https://staging.admin.bobex.nl/';
                case 'ci':
                    return 'https://staging.admin.conversion-impact.be/';
                case 'vbk':
                    return 'https://staging.admin.verbouwkosten.com/';
                case 'govex':
                    return 'https://staging.admin.govex.be/';
                default:
                    return 'https://staging.admin.bobex.be/';
            }
        }
        // Environnement production
        else {
            switch (context.toLowerCase()) {
                case 'be':
                    return 'https://admin.bobex.be/';
                case 'nl':
                    return 'https://admin.bobex.nl/';
                case 'ci':
                    return 'https://admin.conversion-impact.be/';
                case 'vbk':
                    return 'https://admin.verbouwkosten.com/';
                case 'govex':
                    return 'https://admin.govex.be/';
                default:
                    return 'https://admin.bobex.be/';
            }
        }
    },

    getSSOAppUrl() {
        if (window.location.hostname.includes('localhost')) {
            return "http://localhost:3010/";
        }
        return "https://v2.sso.bobex.be/";
    },

    getDomainManagerAppUrl() {
        if (window.location.hostname.includes('localhost')) {
            return "http://localhost:3005/";
        }
        return "https://domainmanager.bobex.be/";
    },

    getMcpDashboardAppUrl() {
        if (window.location.hostname.includes('localhost')) {
            return "http://localhost:3015/";
        }
        return "https://mcp-dashboard.bobex.be/";
    }


}